import { MouseEvent, useRef } from 'react';

import IDropdownObject from '../types/ICustomDropdown';

type DropdownKey = IDropdownObject['key'];

interface IUseDropdownDragSelect {
  isEnabled: boolean;
  selectedKeys: Array<DropdownKey>;
  onChange: (keys: Array<DropdownKey>) => void;
}

interface IDragState {
  originKey: DropdownKey;
  shouldSelect: boolean;
  hasMoved: boolean;
  keys: Array<DropdownKey>;
}

const PRIMARY_BUTTON = 0;
const PRIMARY_BUTTON_PRESSED = 1;

const applySelection = (
  keys: Array<DropdownKey>,
  key: DropdownKey,
  shouldSelect: boolean,
) => {
  if (keys.includes(key) === shouldSelect) {
    return keys;
  }

  return shouldSelect
    ? [...keys, key]
    : keys.filter((selectedKey) =>
      selectedKey !== key);
};

const useDropdownDragSelect = ({
  isEnabled,
  selectedKeys,
  onChange,
}: IUseDropdownDragSelect) => {
  const dragRef = useRef<IDragState | null>(null);

  const handleDragEnd = () => {
    setTimeout(() => {
      dragRef.current = null;
    }, 0);
  };

  const handleDragStart = (key: DropdownKey) =>
    (event: MouseEvent<HTMLElement>) => {
      if (!isEnabled || event.button !== PRIMARY_BUTTON) {
        return;
      }

      dragRef.current = {
        originKey: key,
        shouldSelect: !selectedKeys.includes(key),
        hasMoved: false,
        keys: selectedKeys,
      };
      document.addEventListener('mouseup', handleDragEnd, { once: true });
    };

  const handleDragEnter = (key: DropdownKey) =>
    (event: MouseEvent<HTMLElement>) => {
      const drag = dragRef.current;

      if (!drag) {
        return;
      }

      if (event.buttons !== PRIMARY_BUTTON_PRESSED) {
        dragRef.current = null;
        return;
      }

      const { originKey, shouldSelect, keys } = drag;
      const nextKeys = applySelection(
        applySelection(keys, originKey, shouldSelect),
        key,
        shouldSelect,
      );

      dragRef.current = { ...drag, hasMoved: true, keys: nextKeys };

      if (nextKeys !== keys) {
        onChange(nextKeys);
      }
    };

  const handleClickCapture = (event: MouseEvent<HTMLElement>) => {
    if (dragRef.current?.hasMoved) {
      event.stopPropagation();
    }
  };

  return { handleDragStart, handleDragEnter, handleClickCapture };
};

export default useDropdownDragSelect;
